import { Core } from "@faire/design-tokens";
import { useQuery as useGetBrandVideoQuery } from "@faire/web-api/api/brand-videos/brandVideoToken/page-data/get-hooks";
import { useQuery as useGetProductCatalogQuery } from "@faire/web-api/api/product-catalog/catalogToken/get-hooks";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IDwellTime } from "@faire/web-api/indigofair/data/IDwellTime";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Column, Row } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { usePathParams } from "@faire/web/ui/hooks/usePathParams";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { Suspense, useEffect, useMemo } from "react";
import styled from "styled-components";

import { Footer } from "@faire/retailer/components/Footer";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { popSignUp, SignUpElement, SignUpType } from "@faire/retailer/lib/popSignUp";
import { useDwellService } from "@faire/retailer/services/DwellService";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";
import {
  LARGE_VIDEO_WIDTH,
  MaxWidthContainer,
  MOBILE_CONTAINER_WIDTH,
  TABLET_CONTAINER_WIDTH,
  TINY_MOBILE_CONTAINER_WIDTH,
} from "@faire/retailer/views/video/components/layout";
import { LazyVideoPlayer } from "@faire/retailer/views/video/components/VideoPlayer";
import { FeaturedProducts } from "@faire/retailer/views/video/FeaturedProducts/FeaturedProducts";
import { MoreProducts } from "@faire/retailer/views/video/MoreProducts/MoreProducts";

import { BrandPanel } from "./VideoBrandPanel";
import { VideoBreadcrumbs } from "./VideoBreadcrumbs";

export const VideoPage: React.FC = () => {
  const { videoToken } = usePathParams("/video/videoToken");
  const { userToken } = useUserStore(["userToken"]);
  const viewport = useViewport();
  const { start, stop } = useDwellService(["start", "stop"]);

  const { data: brandVideoData } = useGetBrandVideoQuery(videoToken);
  const brand = useMemo(() => brandVideoData?.brand, [brandVideoData?.brand]);
  const brandVideo = brandVideoData?.brand_video;

  const { data: productCatalogData } = useGetProductCatalogQuery(
    brandVideo?.product_catalog_token ?? "",
    {
      enabled: !!brandVideo?.product_catalog_token && !!userToken,
    }
  );

  useEffect(() => {
    if (!userToken) {
      popSignUp(SignUpType.GENERIC, () => null, {
        element: SignUpElement.AUTO_POPUP,
        sourceId: "video_page_auto_popup_signup",
      });
    }
  }, [userToken]);

  useEffect(() => {
    if (userToken) {
      if (brandVideo?.token) {
        start(brandVideo.token, IDwellTime.Type.BRAND_VIDEO);
      }
      return stop;
    }
    return;
  }, [brandVideo?.token, start, stop, userToken]);

  const featuredProducts = useMemo(() => {
    return (
      productCatalogData?.catalogs[0]?.products?.filter(isNotUndefined) ?? []
    );
  }, [productCatalogData?.catalogs]);

  const featuredProductTokens = useMemo(() => {
    return featuredProducts
      .map((product) => product.token)
      .filter(isNotUndefined);
  }, [featuredProducts]);

  if (!brand || !brand?.token || !brandVideo) {
    return null;
  }
  return (
    <>
      {viewport.isMobile ? (
        <StyledBreadcrumbs
          brandName={brand.name}
          brandToken={brand.token}
          videoName={brandVideo.name}
        />
      ) : null}
      <Page>
        <MaxWidthContainer>
          {viewport.isTabletAndAbove ? (
            <Row justify="space-between">
              <StyledBreadcrumbs
                brandName={brand.name}
                brandToken={brand.token}
                videoName={brandVideo.name}
              />
            </Row>
          ) : null}
          <Container>
            <VideoPanelContainer>
              {brandVideo.video ? (
                <Suspense fallback={null}>
                  <LazyVideoPlayer video={brandVideo.video} />
                </Suspense>
              ) : (
                <StyledCircleSpinner />
              )}
            </VideoPanelContainer>
            {brand ? (
              <TrackedLinkContext.Provider
                value={{
                  type: IClickInfo.Type.BRAND,
                  containerName: "brand_video_page_brand_panel",
                }}
              >
                <BrandPanel brand={brand} brandVideo={brandVideo} />
              </TrackedLinkContext.Provider>
            ) : null}
          </Container>
          {viewport.isTabletAndAbove ? <Spacer size="5x" /> : null}
        </MaxWidthContainer>
        <TrackedLinkContext.Provider
          value={{
            type: IClickInfo.Type.PRODUCT,
            containerName: "brand_video_page",
          }}
        >
          <FeaturedProducts brand={brand} products={featuredProducts} />
          <Spacer size="5x" />
          <Column style={{ backgroundColor: Core.surface.inverse }}>
            {viewport.isTabletAndAbove ? <Spacer size="5x" /> : null}
            <MoreProducts
              containerName="brand_video_page_more_products"
              brand={brand}
              excludedTokens={featuredProductTokens}
            />
            <Spacer size="5x" />
          </Column>
        </TrackedLinkContext.Provider>
        <Footer />
      </Page>
    </>
  );
};

const Page = styled(Column)`
  background-color: ${Core.surface.muted};
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  outline: 1px solid ${Core.border.muted};
  ${responsiveMediaQueries.desktopAndAbove} {
    flex-direction: row;
  }
`;

const VideoPanelContainer = styled.div`
  background-color: ${Core.text.default};
  position: relative;
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    height: ${MOBILE_CONTAINER_WIDTH * (9 / 16)}px;
  }
  @media screen and (max-width: 320px) {
    height: ${TINY_MOBILE_CONTAINER_WIDTH * (9 / 16)}px;
  }
  ${responsiveMediaQueries.tabletAndAbove} {
    height: ${TABLET_CONTAINER_WIDTH * (9 / 16)}px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    height: ${LARGE_VIDEO_WIDTH * (9 / 16)}px;
  }
`;

const StyledCircleSpinner = styled(LoadingSpinner)`
  position: absolute;
  top: calc(50% - 25px);
  left: calc(50% - 25px);
`;

const StyledBreadcrumbs = styled(VideoBreadcrumbs)`
  ${responsiveMediaQueries.mobile} {
    margin: 4px 0px;
    background-color: ${Core.surface.inverse};
  }
`;
